/**
 * The separate P1 sound (FM Technician rebuild B16, D14: "separate P1 sound").
 *
 * A short three-pulse two-tone made with WebAudio — no audio file to ship or
 * cache. BROWSER AUTOPLAY RULES: a page may not start sound before the person
 * has touched it, so the AudioContext is created and resumed inside a user
 * gesture (primeP1Sound, called on the first tap anywhere and on the settings
 * toggle). Until then playP1Sound does nothing and says so (returns false) —
 * it never throws and never fakes it.
 */

type Ctx = AudioContext;
let ctx: Ctx | null = null;

function makeContext(): Ctx | null {
  const w = typeof window !== 'undefined' ? (window as unknown as {
    AudioContext?: new () => Ctx; webkitAudioContext?: new () => Ctx;
  }) : undefined;
  const C = w?.AudioContext ?? w?.webkitAudioContext;
  if (!C) return null;
  try { return new C(); } catch { return null; }
}

/** Call from a user gesture: makes sound possible from now on. */
export function primeP1Sound(): void {
  if (!ctx) ctx = makeContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
}

/** Whether a sound can play now (the person has touched the page). */
export function p1SoundReady(): boolean {
  return !!ctx && ctx.state === 'running';
}

/** Play the P1 sound. False when the browser does not allow it yet. */
export function playP1Sound(): boolean {
  if (!ctx || ctx.state !== 'running') return false;
  try {
    const t0 = ctx.currentTime;
    for (let i = 0; i < 3; i += 1) {
      for (const [freq, offset] of [[988, 0], [740, 0.14]] as const) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = freq;
        const start = t0 + i * 0.4 + offset;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.14);
      }
    }
    return true;
  } catch {
    return false;
  }
}

/** Tests: forget the context. */
export function resetP1SoundForTests(c: Ctx | null = null): void {
  ctx = c;
}
